import { apiErrorMessage } from '@agos/api-client'
import { useGetEmployeesIdWaiterWallet } from '@agos/api-client/hr'
import { postRevenueHandovers } from '@agos/api-client/payments'
import { T, centsToEuros, formatCents, toDateParam, tt } from '@agos/shared'
import {
  BUTTON_SECONDARY,
  cn,
} from '@agos/ui'
import {
  Banknote,
  Calculator,
  CreditCard,
  Globe,
  Loader2,
  Plus,
  Receipt,
  ShoppingBag,
  Ticket,
  Trash2,
  Wallet,
  X,
} from 'lucide-react'
import { useState } from 'react'

import {
  handoverTotals,
  joinLineNotes,
  type HandoverLine,
  type WaiterWalletSummary,
} from './handover-math'

/**
 * Closing a shift, ported from legacy `components/waiter-handover-modal.tsx`.
 *
 * The number at the bottom is counted against physical banknotes, so the
 * arithmetic behind it lives in `handover-math.ts` with tests. What is here is
 * the form: three lists a waiter adds rows to, and one figure that follows.
 *
 * No fuel section, unlike the driver's version — a waiter refuels nothing.
 */

const FIELD_CLASSES =
  'px-2.5 py-1.5 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

let rowCounter = 0
function newRow(): HandoverLine {
  rowCounter += 1
  return { id: `row-${rowCounter}`, amount: '', note: '' }
}

export function WaiterHandoverModal({
  waiterId,
  waiterName,
  onClose,
  onSubmitted,
}: {
  waiterId: number
  waiterName: string
  onClose: () => void
  onSubmitted?: () => void
}) {
  const [vouchers, setVouchers] = useState<HandoverLine[]>([])
  const [purchases, setPurchases] = useState<HandoverLine[]>([])
  const [payouts, setPayouts] = useState<HandoverLine[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const today = toDateParam(new Date())
  const {
    data: wallet,
    isLoading,
    isError,
  } = useGetEmployeesIdWaiterWallet(waiterId, { dateFrom: today, dateTo: today })

  const summary = (wallet as { summary?: WaiterWalletSummary } | undefined)?.summary ?? null
  const totals = handoverTotals({ summary, vouchers, purchases, payouts })

  const submit = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      await postRevenueHandovers({
        totalRevenue: centsToEuros(totals.totalRevenueCents),
        cardPayAmount: centsToEuros(totals.cardOnlineCents),
        cardAmount: centsToEuros(totals.cardTerminalCents),
        vouchersAmount: centsToEuros(totals.voucherCents),
        fuelAmount: 0,
        purchase1Amount: centsToEuros(totals.purchaseCents),
        purchase2Amount: 0,
        payout1Amount: centsToEuros(totals.payoutCents),
        // The schema has two fixed slots, not a list, so several typed rows
        // collapse into the first with their notes joined. The detail survives
        // as text rather than being lost.
        payout1Note: joinLineNotes(payouts, formatCents) || undefined,
        payout2Amount: 0,
        cashToHandOver: centsToEuros(totals.cashToHandOverCents),
        orderCount: summary?.orderCount ?? 0,
      })
      onSubmitted?.()
      onClose()
    } catch (caught) {
      setError(apiErrorMessage(caught, tt('Chyba pri ukladaní')))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          <Calculator className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-foreground">
              <T>Odovzdať tržbu</T>
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {waiterName} · {tt('dnes')}
            </p>
          </div>
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center py-12 text-muted-foreground text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> <T>Načítavam dnešné tržby…</T>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {isError && (
              <p className="text-sm text-red-500">
                <T>Nepodarilo sa načítať dnešnú tržbu</T>
              </p>
            )}

            <div className="bg-muted/50 rounded-xl p-3 space-y-1.5 text-sm">
              <Row
                icon={Wallet}
                label={tt('Celková tržba')}
                value={formatCents(totals.totalRevenueCents)}
                bold
              />
              <Row
                icon={Banknote}
                label={tt('Hotovosť')}
                value={formatCents(
                  totals.totalRevenueCents -
                    totals.cardOnlineCents -
                    totals.cardTerminalCents -
                    totals.cardCourierCents,
                )}
              />
              <Row
                icon={CreditCard}
                label={tt('Karta terminál')}
                value={`−${formatCents(totals.cardTerminalCents)}`}
              />
              <Row
                icon={CreditCard}
                label={tt('Karta kuriér')}
                value={`−${formatCents(totals.cardCourierCents)}`}
              />
              <Row
                icon={Globe}
                label={tt('Online')}
                value={`−${formatCents(totals.cardOnlineCents)}`}
              />
              <p className="text-[11px] text-muted-foreground pt-1">
                {summary?.orderCount ?? 0} <T>objednávok</T>
              </p>
            </div>

            <LineSection
              icon={Ticket}
              title={tt('Stravné lístky')}
              lines={vouchers}
              onChange={setVouchers}
              totalCents={totals.voucherCents}
            />
            <LineSection
              icon={ShoppingBag}
              title={tt('Nákupy')}
              lines={purchases}
              onChange={setPurchases}
              totalCents={totals.purchaseCents}
            />
            <LineSection
              icon={Receipt}
              title={tt('Výdavky')}
              lines={payouts}
              onChange={setPayouts}
              totalCents={totals.payoutCents}
            />

            <div
              className={cn(
                'rounded-xl p-3 flex items-center justify-between border',
                totals.cashToHandOverCents < 0
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-emerald-500/10 border-emerald-500/30',
              )}
            >
              <span className="font-bold text-sm">
                <T>Hotovosť na odovzdanie</T>
              </span>
              <span
                className={cn(
                  'text-xl font-black tabular-nums',
                  totals.cashToHandOverCents < 0 ? 'text-red-500' : 'text-emerald-600',
                )}
              >
                {formatCents(totals.cashToHandOverCents)}
              </span>
            </div>

            {totals.cashToHandOverCents < 0 && (
              <p className="text-[11px] text-red-500">
                {/* Not clamped at zero: a waiter who paid out more than they
                    took is owed money, and hiding that from the person counting
                    the safe is how it goes unpaid. */}
                <T>Výdavky prevyšujú tržbu - reštaurácia dlhuje obsluhe.</T>
              </p>
            )}

            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        )}

        <div className="flex gap-3 px-5 py-4 border-t border-border flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={isSubmitting || isLoading}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-400 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Calculator className="w-4 h-4" />
            )}
            <T>Odovzdať</T>
          </button>
        </div>
      </div>
    </div>
  )
}

function Row({
  icon: Icon,
  label,
  value,
  bold,
}: {
  icon: typeof Wallet
  label: string
  value: string
  bold?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between',
        bold ? 'font-bold text-foreground' : 'text-muted-foreground',
      )}
    >
      <span className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

function LineSection({
  icon: Icon,
  title,
  lines,
  onChange,
  totalCents,
}: {
  icon: typeof Ticket
  title: string
  lines: HandoverLine[]
  onChange: (lines: HandoverLine[]) => void
  totalCents: number
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" /> {title}
        </p>
        <span className="text-xs font-bold tabular-nums">{formatCents(totalCents)}</span>
      </div>

      {lines.map((line, index) => (
        <div key={line.id} className="flex gap-1.5">
          <input
            value={line.amount}
            onChange={(event) =>
              onChange(
                lines.map((candidate, candidateIndex) =>
                  candidateIndex === index
                    ? { ...candidate, amount: event.target.value }
                    : candidate,
                ),
              )
            }
            inputMode="decimal"
            placeholder="0,00"
            aria-label={`${title} - ${tt('suma')}`}
            className={cn(FIELD_CLASSES, 'w-24 tabular-nums')}
          />
          <input
            value={line.note}
            onChange={(event) =>
              onChange(
                lines.map((candidate, candidateIndex) =>
                  candidateIndex === index ? { ...candidate, note: event.target.value } : candidate,
                ),
              )
            }
            placeholder={tt('poznámka')}
            aria-label={`${title} - ${tt('poznámka')}`}
            className={cn(FIELD_CLASSES, 'flex-1')}
          />
          <button
            type="button"
            aria-label={tt('Odstrániť riadok')}
            onClick={() =>
              onChange(lines.filter((_candidate, candidateIndex) => candidateIndex !== index))
            }
            className="px-2 rounded-lg text-muted-foreground hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => onChange([...lines, newRow()])}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-muted-foreground transition-colors"
      >
        <Plus className="w-3 h-3" /> <T>Pridať riadok</T>
      </button>
    </div>
  )
}
